import { action, makeObservable, observable } from "mobx";
import { LLMClassifierImplementation } from "./LLMClassifierImplementation";
import { LLMCLassifierResponse } from "models/LLMs";
import type { ClassifierRolesAccessControl } from "models/LLMs";
import { v4 as uuid } from "uuid";
import { ClassifierType } from "models/ClassifierModel";
import type { ClassifierOutput } from "models/ClassifierModel";
import type ClassifierCategory from "models/ClassifierCategoryId";
import type ClassifierGroup from "models/ClassifierGroup";

export class LLMClassifier {
    public readonly id: string;
    public readonly classifierTypeName: ClassifierType.LLM;
    @observable public readonly implementation: LLMClassifierImplementation;

    @observable public name?: string;
    @observable public description?: string;
    @observable public isPublished: boolean;
    @observable public outputs: ClassifierOutput[];
    @observable public classifierCategoryId?: string;
    @observable public classifierCategory?: ClassifierCategory;
    @observable public classifierGroupId?: string;
    @observable public classifierGroup?: ClassifierGroup;

    @observable public isInternal: boolean;
    @observable public rolesAccessControls: ClassifierRolesAccessControl[];

    @observable public isActive?: boolean;
    @observable public createdBy?: string;
    @observable public modifiedBy?: string;
    @observable public createdOn?: string;
    @observable public modifiedOn?: string;
    @observable public deletedOn?: string;

    constructor(classifier: LLMCLassifierResponse);
    constructor();
    constructor(classifier?: LLMCLassifierResponse) {
        makeObservable(this);

        this.classifierTypeName = ClassifierType.LLM;

        if (!!classifier) {
            this.id = classifier.id;
            this.implementation = new LLMClassifierImplementation(
                classifier.classifierImplementation,
            );
            this.outputs = classifier.classifierOutputs;
            this.name = classifier.name;
            this.description = classifier.description;
            this.isPublished = classifier.isPublished;
            this.classifierCategoryId = classifier.classifierCategory?.id;
            this.classifierCategory = classifier.classifierCategory;
            this.classifierGroupId = classifier.classifierGroup?.id;
            this.classifierGroup = classifier.classifierGroup;
            this.isInternal = classifier.isInternal ?? false;
            this.rolesAccessControls = classifier.rolesAccessControls ?? [];
            this.isActive = classifier.isActive;
            this.createdBy = classifier.createdBy;
            this.createdOn = classifier.createdOn;
            this.modifiedBy = classifier.modifiedBy;
            this.modifiedOn = classifier.modifiedOn;
            this.deletedOn = classifier.deletedOn;
        } else {
            this.id = uuid();
            this.implementation = new LLMClassifierImplementation(this.id);
            this.outputs = [];
            this.isPublished = false;
            this.isInternal = false;
            this.rolesAccessControls = [];
            this.isActive = true;
        }
    }

    @action public setName(name?: string) {
        this.name = name;
    }

    @action public setDescription(description?: string) {
        this.description = description;
    }

    @action public setIsPublished(isPublished: boolean) {
        this.isPublished = isPublished;
    }

    @action public setOutputs(
        outputs: Omit<ClassifierOutput, "classifierId">[],
    ) {
        this.outputs = outputs.map((output) => ({
            ...output,
            classifierId: this.id,
        }));
    }

    @action public addOutput(output?: Omit<ClassifierOutput, "classifierId">) {
        if (!output) {
            output = {
                id: uuid(),
                result: "",
            };
        }

        this.outputs.push({
            ...output,
            classifierId: this.id,
        });
    }

    @action public updateOutputById(outputId: string, result: string) {
        const output = this.outputs.find((o) => o.id === outputId);
        if (!output) return;
        output.result = result;
    }

    @action public removeOutputById(outputId: string) {
        this.outputs = this.outputs.filter((o) => o.id !== outputId);
    }

    @action public setIsInternal(isInternal: boolean) {
        this.isInternal = isInternal;
    }

    @action public setRolesAccessControls(
        controls: ClassifierRolesAccessControl[],
    ) {
        this.rolesAccessControls = controls;
    }

    @action public setClassifierCategory(
        classifierCategory?: ClassifierCategory,
    ) {
        this.classifierCategory = classifierCategory;
        this.classifierCategoryId = classifierCategory?.id;
    }

    @action public setClassifierGroup(classifierGroup?: ClassifierGroup) {
        this.classifierGroup = classifierGroup;
        this.classifierGroupId = classifierGroup?.id;
    }

    @action public setCreatedBy(createdBy?: string) {
        this.createdBy = createdBy;
    }

    @action public setModifiedBy(modifiedBy?: string) {
        this.modifiedBy = modifiedBy;
    }

    @action public setCreatedOn(createdOn?: string) {
        this.createdOn = createdOn;
    }

    @action public setModifiedOn(modifiedOn?: string) {
        this.modifiedOn = modifiedOn;
    }

    @action public setDeletedOn(deletedOn?: string) {
        this.deletedOn = deletedOn;
    }

    public toObject(): LLMCLassifierResponse {
        if (!this.name) throw new Error("Name is required");
        if (!this.implementation) throw new Error("Implementation is required");

        const implementation = this.implementation.toObject();

        return {
            id: this.id,
            name: this.name,
            description: this.description,
            isPublished: this.isPublished,
            classifierCategory: this.classifierCategory,
            classifierCategoryId: this.classifierCategory?.id,
            classifierGroup: this.classifierGroup,
            classifierGroupId: this.classifierGroup?.id,
            classifierImplementation: implementation,
            classifierOutputs: this.outputs,
            isInternal: this.isInternal,
            rolesAccessControls: this.rolesAccessControls,
            isActive: this.isActive,
            createdBy: this.createdBy,
            modifiedBy: this.modifiedBy,
            createdOn: this.createdOn,
            modifiedOn: this.modifiedOn,
            deletedOn: this.deletedOn,
        } as LLMCLassifierResponse;
    }
}
